import {
  EMPTY_JEWEL_SOCKET_PROFILE,
  type EquipmentTotals,
  type JewelSocketProfile,
  NO_EQUIPMENT_TOTALS,
} from '@kenocyber/shared';
import { resolveJewelEffects } from '../damage/jewel-effects';
import { composeMaxHealthMilli, HEALTH_MILLI_PER_UNIT } from '../hull/max-health';
import { effectiveBaseHealth } from './attributes';
import {
  equipmentBaseHealth,
  equipmentMaxHealthBps,
  NO_PASSIVES,
  type PassiveAttributes,
  passiveBaseHealth,
  passiveMaxHealthBps,
} from './passive-attributes';

/**
 * **A vida máxima de uma CONTA, resolvida uma vez e usada nos dois modos.**
 *
 * ## O problema que este ficheiro resolve (gate V28G.S)
 *
 * O V28G.R deu às duas simulações a mesma ARITMÉTICA - `hull/max-health.ts` - e
 * deixou-lhes fontes diferentes: a caça somava os pontos, os artefactos, o
 * equipamento e as joias, e o duelo lia `PVP_MAX_HEALTH = 1 000`. A mesma nave
 * tinha duas vidas máximas conforme o ecrã em que estava.
 *
 * A regra de produto é uma linha: **a nave tem uma vida máxima**. 100 na caça é
 * 100 no duelo; 16 000 depois do Coração do Titã é 16 000 no duelo. Sem
 * conversão, sem normalização e sem multiplicador entre os modos.
 *
 * Então as FONTES vivem aqui, num sítio só, e as duas pontas atravessam por
 * elas. A caça chama-as a cada leitura, com o que a sua sessão congelou; a
 * autoridade chama-as ao emitir um bilhete, com o que a conta tem na base. É a
 * mesma função, com as mesmas parcelas, na mesma ordem.
 *
 * ## A composição, e ela é a do `CLAUDE.md`
 *
 *     planaAntesDosPercentuais =
 *       vidaBase + pontosAlocados + planaDeArtefactos + planaDeEquipamento
 *
 *     bonusPercentualTotal =
 *       bpsDeArtefactos + bpsDeEquipamento + CoraçãoDoTitã
 *
 *     vidaMaximaFinal = plana x (10 000 + bonus) / 10 000
 *
 * Todas as parcelas planas somadas primeiro, todas as percentagens somadas a
 * seguir, e a multiplicação **uma única vez**. O Coração do Titã entra uma vez,
 * como uma parcela da soma - e é por isso que não existe caminho por onde ele
 * possa ser aplicado duas vezes: nada fora daqui multiplica vida por
 * percentagem, e o duelo recebe o número já composto.
 *
 * ## Onde ele vive, e porque não vive em `hull/`
 *
 * `hull/` é uma FOLHA: não importa `hunt/` nem `pvp/`, e é isso que deixa as
 * duas simulações importarem-no sem uma arrastar a outra. Isto precisa de
 * `HUNT_BALANCE` (a vida-base da nave e o valor de um ponto), portanto vive do
 * lado que já tem esses números - ao lado da ordem de cálculo que o `CLAUDE.md`
 * fixa em `hunt/passive-attributes.ts`.
 *
 * **Nada em `pvp/` importa este ficheiro, e é deliberado.** A sala não resolve
 * uma vida máxima: ela RECEBE uma, pronta, no bilhete assinado. Um `import`
 * daqui para lá seria a sala a ganhar a capacidade de a recompor, que é
 * exatamente a dupla aplicação que este gate proíbe.
 *
 * ## Baby Aliens
 *
 * Não concedem vida hoje, e este gate não lhes inventa esse efeito. Se um dia
 * concederem, entram por `PassiveAttributes` como qualquer outra parcela - nesta
 * soma, e não numa segunda.
 */

/**
 * Tudo o que decide a vida máxima de uma conta, e nada mais.
 *
 * Quatro parcelas, e todas elas são dados já DERIVADOS de linhas possuídas -
 * nenhuma é um total acumulado numa coluna. Não há aqui um id de instância, uma
 * skin, um socket nem uma conta: o que atravessa são efeitos, não posse.
 */
export interface AccountMaxHealthSources {
  /** Os pontos de vida alocados, como a progressão os guarda. */
  readonly healthPoints: bigint;
  /** Os artefactos, derivados do cofre a cada leitura. */
  readonly passives: PassiveAttributes;
  /** Os cinco componentes equipados. */
  readonly equipment: EquipmentTotals;
  /** As joias encaixadas na skin equipada. Hoje só o Coração do Titã pesa aqui. */
  readonly jewels: JewelSocketProfile;
}

/**
 * A conta vazia: sem pontos, sem artefactos, sem equipamento e sem joias.
 *
 * É o estado de uma conta acabada de criar, e a sua vida máxima é a vida-base
 * canónica da nave - **não** um mil artificial. Isso é o que faz alguém sem nada
 * entrar no duelo com o casco que a caça lhe dá.
 */
export const NO_ACCOUNT_MAX_HEALTH_SOURCES: AccountMaxHealthSources = Object.freeze({
  healthPoints: 0n,
  passives: NO_PASSIVES,
  equipment: NO_EQUIPMENT_TOTALS,
  jewels: EMPTY_JEWEL_SOCKET_PROFILE,
});

/**
 * Passo 1: toda a vida PLANA, antes de qualquer percentagem.
 *
 * A base da nave mais os pontos, mais a plana dos artefactos, mais a plana do
 * equipamento. Somar uma destas depois da multiplicação faria a percentagem
 * valer menos a quem carrega a Nave-Mãe, que é o degrau que a ordem do
 * `CLAUDE.md` existe para impedir.
 */
export function accountFlatMaxHealth(sources: AccountMaxHealthSources): number {
  return (
    effectiveBaseHealth(sources.healthPoints) +
    passiveBaseHealth(sources.passives) +
    equipmentBaseHealth(sources.equipment)
  );
}

/**
 * Passo 2: a SOMA de todas as percentagens de vida máxima, em basis points.
 *
 * Artefactos, equipamento e Coração do Titã. Somadas, nunca multiplicadas em
 * sequência: `+60 %` ao lado de `+20 %` são `+80 %`, e nunca `1,60 x 1,20`.
 */
export function accountMaxHealthBps(sources: AccountMaxHealthSources): bigint {
  return (
    passiveMaxHealthBps(sources.passives) +
    equipmentMaxHealthBps(sources.equipment) +
    resolveJewelEffects(sources.jewels).maxHealthBps
  );
}

/**
 * **A vida máxima final, em MILLI, exata - e a única autoridade** (V28G.S.1).
 *
 * ## Porque milli, e porque `bigint`
 *
 * Porque a vida máxima de uma conta **não é um número inteiro de pontos**. A
 * auditoria do V28G.S mediu-o: `flatMaxHealth` de equipamento é sorteado por
 * MILLI sobre `[2 000, 12 000]` - dez mil e um valores, dos quais onze são
 * múltiplos de mil - e `maxHealthPercent` é sorteado por BPS sobre `[150, 900]`.
 * Uma Câmara comum produz 102,001, 102,5 ou 102,999 de vida, e um Mother Ship
 * nível 1 sozinho produz 108,12.
 *
 * O milésimo é a menor unidade que qualquer fonte legal produz, portanto é a
 * unidade em que a vida é EXATA. Um `bigint` porque a progressão não tem tecto:
 * 2^40 pontos valem 1,1 x 10^16 milli, acima de `Number.MAX_SAFE_INTEGER` - um
 * `number` aqui seria um teto de progressão escrito num tipo.
 *
 * ## Uma autoridade, e nenhuma conversão por modo
 *
 * Este `bigint` é o valor. A caça deriva dele o seu `number` fracionário
 * ({@link accountMaxHealth}); o duelo transporta-o tal e qual. **Não há
 * truncatura, arredondamento nem conversão em nenhum dos dois caminhos** - foi
 * exatamente isso que a `accountWholeMaxHealth` do V28G.S fazia, e foi por isso
 * que ela foi removida.
 *
 * ## Sem tecto, e com uma recusa
 *
 * Não há máximo de jogo: capar aqui seria capar o que um item concede. O mínimo
 * é um milésimo de vida - a vida-base da nave sozinha vale 100 000 milli e
 * nenhuma percentagem negativa existe - portanto a recusa marca um caminho de
 * emissão avariado, e não um casco.
 */
export function accountMaxHealthMilli(sources: AccountMaxHealthSources): bigint {
  const milli = composeMaxHealthMilli(accountFlatMaxHealth(sources), accountMaxHealthBps(sources));
  if (milli < 1n) {
    throw new RangeError(`an account hull has at least one milli of health, got ${milli}`);
  }
  return milli;
}

/**
 * **A MESMA vida máxima, na unidade fracionária da caça.**
 *
 * `HuntSimulation.maxHealth()` é exatamente isto e nada mais.
 *
 * ## Uma DERIVAÇÃO, e não uma segunda composição
 *
 * Ela divide o `bigint` autoritativo por mil, e é tudo. Antes chamava
 * `composeMaxHealth`, que fazia a mesma aritmética por outro caminho - duas
 * expressões a concordar por construção, o que é uma a mais. Agora há um número
 * e uma leitura dele, e `1000 x accountMaxHealth === Number(accountMaxHealthMilli)`
 * é verdade por definição em vez de por coincidência.
 *
 * A física da caça é em vírgula flutuante desde que existe, e migrá-la seria
 * mudar todos os números do jogo para resolver um problema que não está lá: a
 * caça **já** carregava a fração. O que este gate arranjou foi o duelo, que a
 * perdia.
 */
export function accountMaxHealth(sources: AccountMaxHealthSources): number {
  return Number(accountMaxHealthMilli(sources)) / HEALTH_MILLI_PER_UNIT;
}
